import { Suspense, useState } from "react";
import { BrandName } from "@/shared/branding";
import { Outlet } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { RoutePermissionGate } from "@/auth/RoutePermissionGate";
// ═ COMPANY-STRIP BEGIN ═ COMPANY-CUT-1 · 后台版式（顶部导航）
import { useAdminLayout } from "@/theme/useTheme";
import { TopNavLayout } from "./TopNavLayout";
// ═ COMPANY-STRIP END ═

const SIDEBAR_COLLAPSED_KEY = "ota-sidebar-collapsed";

// ═ COMPANY-STRIP BEGIN ═
/**
 * 后台外壳按主题的「后台版式」选：sidebar = 现在的侧栏（默认）· topnav = 顶部模块导航 + 左侧二级细栏（THEME-1 主题 A）。
 * 页面内容组件两套共用（都是 <Outlet/>）。
 */
export function Layout() {
  return useAdminLayout() === "topnav" ? <TopNavLayout /> : <SidebarLayout />;
}
// ═ COMPANY-STRIP END ═
// ═ COMPANY-ONLY BEGIN ═
// /** 后台外壳：左侧栏（公司版固定）。 */
// export function Layout() {
//   return <SidebarLayout />;
// }
// ═ COMPANY-ONLY END ═

function SidebarLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // 桌面端整条侧栏收起（持久化 → 刷新保留全屏偏好）
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1",
  );
  const toggleDesktop = () =>
    setDesktopCollapsed((v) => {
      const next = !v;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });

  return (
    <div className="flex min-h-screen bg-paper">
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        desktopCollapsed={desktopCollapsed}
      />

      {/* min-w-0 防止内容在侧栏隐藏后撑破 flex 行 */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* mobile hamburger row */}
        <div className="flex h-12 items-center border-b border-slate-200 bg-white px-4 md:hidden">
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100"
          >
            <Menu className="h-5 w-5 text-slate-700" />
          </button>
          <span className="ml-3 text-sm font-semibold text-slate-900"><BrandName /></span>
        </div>

        <Topbar
          onToggleSidebar={() => setMobileOpen((v) => !v)}
          desktopCollapsed={desktopCollapsed}
          onToggleDesktopSidebar={toggleDesktop}
        />

        <main className="flex-1">
          {/* C1 路由级 code-split 的 Suspense 边界（按需 chunk 加载时显轻量占位·不闪整屏） */}
          <Suspense fallback={<RouteFallback />}>
            <RoutePermissionGate>
              <Outlet />
            </RoutePermissionGate>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/** 路由 chunk 加载中的轻量占位（只在首次进某页·该 chunk 未缓存时短暂出现） */
function RouteFallback() {
  return (
    <div className="flex h-64 items-center justify-center text-sm text-slate-400">
      <span className="inline-flex items-center gap-2">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
        加载中…
      </span>
    </div>
  );
}
